import React, { Component } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { AppBadge } from "../../components/ui";

import { toastService } from "../../services/toastService";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { utils } from "../../utils/utils";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import { individualDocumentApiService } from "../../services/api/individual/IndividualDocumentApiService";
import { userApiService } from "../../services/api/users/UsersApiService";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import { fileService } from "../../services/FileService";
import { KycSubmissionDocument } from "../../models/KycSubmission";
import { AdditionalDocumentUploadChannel, AdditionalDocumentWorkflowStatus, IndividualDocumentModel } from "../../models/IndividualDocument";
import { DocumentTypeModel } from "../../models/DocumentTypeModel";
import { Result } from "../../models/Result";
import {
    getCurrentOperatorUserId,
    isIndividualDocumentsApproveAllowed,
    isIndividualDocumentsDownloadAllowed,
    isIndividualDocumentsManageCustomerVisibilityAllowed,
    isIndividualDocumentsManageInternalNoteAllowed,
    isIndividualDocumentsReviewAllowed,
    isIndividualDocumentsUploadAllowed,
    isSuperAdmin,
} from "../../authGuard";

// Convenience only — CRM and File Storage reject anything else regardless. Size stays a server rule until
// the product decides between 5 MB and 10 MB.
const ALLOWED_UPLOAD_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"];
// CRM pages this list (default 50, cap 100) and returns no total, so a full page means there may be more.
const DOCUMENTS_PAGE_SIZE = 50;
const SLOW_AFTER_MS = 3000;

// Same limit as KYC documents (CRM KycSubmissionConstants.DocumentFileNameLengthLimit, TNPDEVAPPS-1997):
// an officer-uploaded file name must be shorter than this.
const DOCUMENT_FILE_NAME_LENGTH_LIMIT = 20;

// KYC documents carry only a file name; the type decides how the browser shows the preview.
const mimeTypeFromFileName = (fileName: string): string => {
    const extension = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
    switch (extension) {
        case ".pdf": return "application/pdf";
        case ".png": return "image/png";
        case ".jpg":
        case ".jpeg": return "image/jpeg";
        default: return "application/octet-stream";
    }
};

// What the preview dialog shows: an additional document (bytes through CRM) or a KYC document (bytes from
// File Storage). Both open in the same dialog.
interface PreviewTarget {
    fileName: string;
    mimeType: string;
    download: () => void;
}

const base64ToBlob = (base64: string, mimeType: string): Blob =>
    new Blob([Uint8Array.from(atob(base64), (character) => character.charCodeAt(0))], { type: mimeType });

interface UserDocumentsProps {
    individualId: number;
    // The individual's current KYC submission. Passed only by the profile's Documents tab, which then also lists
    // that submission's KYC documents; the scorecard already shows them in its own Documents card.
    currentKycSubmissionId?: number | null;
}

interface UserDocumentsState {
    isLoading: boolean;
    loadFailed: boolean;
    pageNumber: number;
    documents: IndividualDocumentModel[];
    documentTypes: DocumentTypeModel[];
    typesLoadFailed: boolean;
    documentTypeNameById: Record<number, string>;
    // Draft note text per document id, so typing in one row doesn't touch the others.
    noteDraftByDocumentId: Record<number, string>;
    rejectionDraftByDocumentId: Record<number, string>;
    busyDocumentId: number | null;
    previewDocument: PreviewTarget | null;
    previewUrl: string | null;
    previewLoading: boolean;
    previewSlow: boolean;
    uploadFile: File | null;
    uploadInputKey: number;
    uploadDocumentTypeId: number | null;
    uploadIdempotencyKey: string | null;
    isUploading: boolean;
    uploadSlow: boolean;
    downloadingDocumentId: number | null;
    // List load, row action or download running past SLOW_AFTER_MS.
    waitSlow: boolean;
    kycDocuments: KycSubmissionDocument[];
    kycDocumentsLoadFailed: boolean;
    openingKycFileStorageId: string | null;
}

// Small, self-contained EN/BG map — scoped to this component per TNPDEVAPPS-1660 (statuses,
// buttons, empty/error copy). Not a portal-wide i18n framework: none exists here yet.
const strings = {
    en: {
        statusUploaded: "Uploaded",
        statusInReview: "In Review",
        statusConfirmed: "Confirmed",
        statusRejected: "Rejected",
        preview: "Preview",
        download: "Download",
        startReview: "Start Review",
        confirm: "Confirm",
        reject: "Reject",
        upload: "Upload",
        saveNote: "Save note",
        rejectionReasonPlaceholder: "Rejection reason",
        selectDocumentType: "Select document type",
        readOnlyDecided: "Read-only — document is already decided.",
        cannotApproveOwn: "You uploaded this document — cannot confirm or reject your own upload.",
        chooseFileFirst: "Choose a file first.",
        readFileFailed: "Could not read the file. Choose it again.",
        unsupportedFileType: "Only PDF, PNG and JPG files can be uploaded.",
        fileNameTooLong: "File name must be less than 20 characters. Rename the file and choose it again.",
        duplicateFileName: "A document with file name {0} already exists for this customer.",
        kycDocumentsTitle: "KYC documents (current submission)",
        kycDocumentsLoadFailed: "KYC documents could not be loaded.",
        chooseTypeFirst: "Choose a document type first.",
        enterRejectionReason: "Enter a rejection reason first.",
        documentUploaded: "Document uploaded.",
        reviewStarted: "Review started.",
        documentConfirmed: "Document confirmed.",
        documentRejected: "Document rejected.",
        noteSaved: "Note saved.",
        visibleToCustomer: "Document made visible to customer.",
        hiddenFromCustomer: "Document hidden from customer.",
        previewLoading: "Loading preview…",
        previewStillLoading: "Still loading the file… You can close this and try again later.",
        listLoadFailed: "Documents could not be loaded.",
        typesLoadFailed: "Document types could not be loaded.",
        retry: "Retry",
        cancel: "Cancel",
        fileLoadNoResponse: "The file took too long to load. Try again.",
        uploadNoResponse: "The upload took too long. Try again — it will not create a duplicate.",
        actionNoResponse: "No response from the server. Reload the list and check the document's status before trying again.",
        stillUploading: "Still uploading…",
        stillWorking: "Still working…",
        stillLoading: "Still loading the documents…",
        uploadCancelled: "Upload cancelled. The file is still selected, so you can try again.",
        previousPage: "Previous",
        nextPage: "Next",
        previewNotAvailable: "Preview is not available for this file type",
        downloadInstead: "Download instead"
    },
    bg: {
        statusUploaded: "Качен",
        statusInReview: "В преглед",
        statusConfirmed: "Потвърден",
        statusRejected: "Отхвърлен",
        preview: "Преглед",
        download: "Изтегли",
        startReview: "Започни преглед",
        confirm: "Потвърди",
        reject: "Отхвърли",
        upload: "Качи",
        saveNote: "Запази бележка",
        rejectionReasonPlaceholder: "Причина за отхвърляне",
        selectDocumentType: "Изберете тип документ",
        readOnlyDecided: "Само за четене — документът вече е решен.",
        cannotApproveOwn: "Вие качихте този документ — не можете да го потвърдите или отхвърлите.",
        chooseFileFirst: "Първо изберете файл.",
        readFileFailed: "Файлът не можа да бъде прочетен. Изберете го отново.",
        unsupportedFileType: "Може да се качват само PDF, PNG и JPG файлове.",
        fileNameTooLong: "Името на файла трябва да е по-кратко от 20 символа. Преименувайте файла и го изберете отново.",
        duplicateFileName: "Вече има документ с име {0} за този клиент.",
        kycDocumentsTitle: "KYC документи (текуща заявка)",
        kycDocumentsLoadFailed: "KYC документите не можаха да бъдат заредени.",
        chooseTypeFirst: "Първо изберете тип документ.",
        enterRejectionReason: "Първо въведете причина за отхвърляне.",
        documentUploaded: "Документът е качен.",
        reviewStarted: "Прегледът започна.",
        documentConfirmed: "Документът е потвърден.",
        documentRejected: "Документът е отхвърлен.",
        noteSaved: "Бележката е запазена.",
        visibleToCustomer: "Документът е видим за клиента.",
        hiddenFromCustomer: "Документът е скрит от клиента.",
        previewLoading: "Зареждане на преглед…",
        previewStillLoading: "Файлът все още се зарежда… Можете да затворите и да опитате по-късно.",
        listLoadFailed: "Документите не можаха да бъдат заредени.",
        typesLoadFailed: "Типовете документи не можаха да бъдат заредени.",
        retry: "Опитай отново",
        cancel: "Отказ",
        fileLoadNoResponse: "Зареждането на файла отне твърде дълго. Опитайте отново.",
        uploadNoResponse: "Качването отне твърде дълго. Опитайте отново — няма да се създаде дубликат.",
        actionNoResponse: "Няма отговор от сървъра. Презаредете списъка и проверете статуса на документа, преди да опитате отново.",
        stillUploading: "Все още се качва…",
        stillWorking: "Все още се обработва…",
        stillLoading: "Документите все още се зареждат…",
        uploadCancelled: "Качването е прекратено. Файлът остава избран и можете да опитате отново.",
        previousPage: "Предишна",
        nextPage: "Следваща",
        previewNotAvailable: "Прегледът не е наличен за този тип файл",
        downloadInstead: "Изтегли вместо това"
    }
} as const;

type StringKey = keyof typeof strings.en;

const getLang = (): "en" | "bg" => (navigator.language?.toLowerCase().startsWith("bg") ? "bg" : "en");

const t = (key: StringKey): string => strings[getLang()][key];

const workflowStatusLabels: Record<AdditionalDocumentWorkflowStatus, StringKey> = {
    [AdditionalDocumentWorkflowStatus.Uploaded]: "statusUploaded",
    [AdditionalDocumentWorkflowStatus.InReview]: "statusInReview",
    [AdditionalDocumentWorkflowStatus.Confirmed]: "statusConfirmed",
    [AdditionalDocumentWorkflowStatus.Rejected]: "statusRejected",
};

const workflowStatusBadgeMap: Record<AdditionalDocumentWorkflowStatus, string> = {
    [AdditionalDocumentWorkflowStatus.Uploaded]: "info",
    [AdditionalDocumentWorkflowStatus.InReview]: "warning",
    [AdditionalDocumentWorkflowStatus.Confirmed]: "success",
    [AdditionalDocumentWorkflowStatus.Rejected]: "danger",
};

const uploadChannelLabels: Record<AdditionalDocumentUploadChannel, string> = {
    [AdditionalDocumentUploadChannel.WalletMobile]: "Wallet (Mobile)",
    [AdditionalDocumentUploadChannel.WalletWeb]: "Wallet (Web)",
    [AdditionalDocumentUploadChannel.MerchantPortal]: "Merchant Portal",
    [AdditionalDocumentUploadChannel.Onboarding]: "Onboarding",
};

// CRM rejects note/visibility changes once a document is Confirmed or Rejected
// (UpdateInternalNoteAsync_WhenDocumentIsDecided_RejectsTheChange) — disable the controls instead
// of letting the operator hit a guaranteed-to-fail save.
const isDecided = (document: IndividualDocumentModel): boolean =>
    document.workflowStatus === AdditionalDocumentWorkflowStatus.Confirmed ||
    document.workflowStatus === AdditionalDocumentWorkflowStatus.Rejected;

// Real CRM workflow (TNPDEVAPPS-1652): Confirm/Reject appear only once a document is In Review.
// Errors surface via toast, not silently.
export default class UserDocumentsComponent extends Component<UserDocumentsProps, UserDocumentsState> {
    constructor(props: UserDocumentsProps) {
        super(props);

        this.state = {
            isLoading: false,
            loadFailed: false,
            pageNumber: 1,
            documents: [],
            documentTypes: [],
            typesLoadFailed: false,
            documentTypeNameById: {},
            noteDraftByDocumentId: {},
            rejectionDraftByDocumentId: {},
            busyDocumentId: null,
            previewDocument: null,
            previewUrl: null,
            previewLoading: false,
            previewSlow: false,
            uploadFile: null,
            uploadInputKey: 0,
            uploadDocumentTypeId: null,
            uploadIdempotencyKey: null,
            isUploading: false,
            uploadSlow: false,
            downloadingDocumentId: null,
            waitSlow: false,
            kycDocuments: [],
            kycDocumentsLoadFailed: false,
            openingKycFileStorageId: null,
        };
    }

    componentDidMount() {
        this.loadDocuments();
        this.loadDocumentTypes();
        this.loadKycDocuments();
    }

    private previewAbort: AbortController | null = null;
    private previewSlowTimer: ReturnType<typeof setTimeout> | null = null;
    private uploadAbort: AbortController | null = null;
    private uploadSlowTimer: ReturnType<typeof setTimeout> | null = null;
    private waitSlowTimer: ReturnType<typeof setTimeout> | null = null;

    // One timer for the list load, row actions and download: they never run at the same time.
    componentDidUpdate(_prevProps: UserDocumentsProps, prevState: UserDocumentsState) {
        const isWaiting = (state: UserDocumentsState) =>
            state.isLoading || state.busyDocumentId !== null || state.downloadingDocumentId !== null;
        if (isWaiting(prevState) === isWaiting(this.state)) {
            return;
        }
        if (this.waitSlowTimer) {
            clearTimeout(this.waitSlowTimer);
            this.waitSlowTimer = null;
        }
        if (isWaiting(this.state)) {
            this.waitSlowTimer = setTimeout(() => this.setState({ waitSlow: true }), SLOW_AFTER_MS);
        } else {
            this.setState({ waitSlow: false });
        }
    }

    componentWillUnmount() {
        if (this.waitSlowTimer) {
            clearTimeout(this.waitSlowTimer);
        }
        this.stopPreviewRequest();
        this.uploadAbort?.abort();
        if (this.uploadSlowTimer) {
            clearTimeout(this.uploadSlowTimer);
        }
        this.revokePreviewUrl();
    }

    // Same lookup AddManualKyc.tsx uses for the old KYC docs block — DocumentTypeMaster
    // (id + displayName), not the legacy enum.
    loadDocumentTypes = async () => {
        this.setState({ typesLoadFailed: false });
        const result = await userApiService.getIndividualDocumentsTypes();
        if (!result.success) {
            this.setState({ typesLoadFailed: true });
            return;
        }
        const documentTypeNameById: Record<number, string> = {};
        result.data.forEach((documentType: DocumentTypeModel) => {
            documentTypeNameById[documentType.id] = documentType.displayName;
        });
        this.setState({ documentTypes: result.data, documentTypeNameById });
    };

    // An inline error, not just a toast: an empty table would otherwise read as "no documents".
    loadDocuments = async (pageNumber: number = this.state.pageNumber) => {
        this.setState({ isLoading: true, loadFailed: false });

        const result = await individualDocumentApiService.getForIndividual(this.props.individualId, pageNumber, DOCUMENTS_PAGE_SIZE);

        this.setState({ isLoading: false });
        if (!result.success) {
            this.setState({ loadFailed: true, documents: [] });
            return;
        }
        this.setState({ documents: result.data, pageNumber });
    };

    // Only the current submission's KYC documents, read-only, through the same endpoint the KYC tab's ZIP uses.
    loadKycDocuments = async () => {
        if (!this.props.currentKycSubmissionId) {
            return;
        }
        this.setState({ kycDocumentsLoadFailed: false });
        const result = await kycSubmissionApiService.getDocuments(this.props.currentKycSubmissionId);
        if (!result.success) {
            this.setState({ kycDocumentsLoadFailed: true });
            return;
        }
        this.setState({ kycDocuments: result.data });
    };

    // KYC documents are read straight from File Storage by their storage id, as the KYC screens already do.
    onDownloadKycDocument = async (kycDocument: KycSubmissionDocument) => {
        this.setState({ openingKycFileStorageId: kycDocument.fileStorageId });
        const result = await fileService.GetFile(kycDocument.fileStorageId);
        this.setState({ openingKycFileStorageId: null });
        if (!result.success) {
            toastService.error(this.errorMessage(result, "fileLoadNoResponse"));
            return;
        }

        const url = URL.createObjectURL(base64ToBlob(result.data, mimeTypeFromFileName(kycDocument.fileName)));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", kycDocument.fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Same dialog as an additional document. fileService.GetFile takes no signal, so the controller only marks
    // the preview as closed or replaced, and a late response is dropped.
    onPreviewKycDocument = async (kycDocument: KycSubmissionDocument) => {
        const mimeType = mimeTypeFromFileName(kycDocument.fileName);
        const abort = this.startPreview({
            fileName: kycDocument.fileName,
            mimeType,
            download: () => this.onDownloadKycDocument(kycDocument),
        });

        const result = await fileService.GetFile(kycDocument.fileStorageId);
        if (abort.signal.aborted) {
            return;
        }
        this.stopPreviewRequest();
        this.setState({ previewLoading: false });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "fileLoadNoResponse"));
            this.setState({ previewDocument: null });
            return;
        }
        this.setState({ previewUrl: URL.createObjectURL(base64ToBlob(result.data, mimeType)) });
    };

    // The Super Admin may decide their own uploads (TNPDEVAPPS-2033); CRM applies the same exemption.
    isSelfApproval = (document: IndividualDocumentModel): boolean => {
        if (isSuperAdmin()) {
            return false;
        }
        const currentUserId = getCurrentOperatorUserId();
        return currentUserId !== null && currentUserId === document.uploadedBy;
    };

    // The input keeps its value so the browser shows the chosen file; a successful upload resets it by
    // changing uploadInputKey, which also lets the same file be chosen again. A newly chosen file starts a
    // new idempotency key; a retry of the same file never reaches this handler, so it keeps its key, and an
    // empty pick (dialog closed) leaves the key alone.
    onUploadFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadFile = event.target.files?.[0] ?? null;
        const extension = uploadFile?.name.slice(uploadFile.name.lastIndexOf(".")).toLowerCase() ?? "";
        if (uploadFile && !ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
            // A file picked through "All files" — reset the input so it doesn't keep showing the rejected name.
            toastService.error(t("unsupportedFileType"), "Error", true);
            this.setState((state) => ({ uploadFile: null, uploadInputKey: state.uploadInputKey + 1 }));
            return;
        }
        if (uploadFile && uploadFile.name.length >= DOCUMENT_FILE_NAME_LENGTH_LIMIT) {
            toastService.error(t("fileNameTooLong"), "Error", true);
            this.setState((state) => ({ uploadFile: null, uploadInputKey: state.uploadInputKey + 1 }));
            return;
        }
        if (uploadFile && this.isFileNameTaken(uploadFile.name)) {
            toastService.error(t("duplicateFileName").replace("{0}", uploadFile.name), "Error", true);
            this.setState((state) => ({ uploadFile: null, uploadInputKey: state.uploadInputKey + 1 }));
            return;
        }

        if (uploadFile) {
            this.setState({ uploadFile, uploadIdempotencyKey: null });
        } else {
            this.setState({ uploadFile });
        }
    };

    // Same rule as CRM's UploadAsync: case-insensitive, a Rejected document does not block (a new upload is how it
    // is corrected), a legacy row does. Only the loaded page is checked here; CRM checks every document.
    isFileNameTaken = (fileName: string): boolean => {
        const lowerFileName = fileName.toLowerCase();
        return this.state.documents.some((document) =>
            document.workflowStatus !== AdditionalDocumentWorkflowStatus.Rejected &&
            document.originalFileName.toLowerCase() === lowerFileName);
    };

    // Kept in state (not regenerated per click) so a retry after a failed upload reuses the same
    // key instead of creating a duplicate document — same convention as
    // AdditionalCustomerDocumentsPanel's notification idempotency key.
    onUpload = async () => {
        const { uploadFile, uploadDocumentTypeId } = this.state;
        if (!uploadFile) {
            toastService.error(t("chooseFileFirst"), "Error", true);
            return;
        }
        if (!uploadDocumentTypeId) {
            toastService.error(t("chooseTypeFirst"), "Error", true);
            return;
        }

        const idempotencyKey = this.state.uploadIdempotencyKey
            ?? `additional-document-upload-${this.props.individualId}-${Date.now()}`;
        this.setState({ isUploading: true, uploadSlow: false, uploadIdempotencyKey: idempotencyKey });
        const abort = new AbortController();
        this.uploadAbort = abort;
        this.uploadSlowTimer = setTimeout(() => this.setState({ uploadSlow: true }), SLOW_AFTER_MS);

        // FileReader fails when the file was moved, deleted or lost permission after it was picked.
        // Nothing reached CRM, so unlock the form and make the operator pick the file again.
        let fileData: string;
        try {
            fileData = (await utils.convertFileToBase64(uploadFile)) as string;
        } catch {
            this.finishUpload();
            toastService.error(t("readFileFailed"), "Error", true);
            this.setState((state) => ({ uploadFile: null, uploadInputKey: state.uploadInputKey + 1 }));
            return;
        }

        const result = await individualDocumentApiService.upload({
            individualId: this.props.individualId,
            documentTypeId: uploadDocumentTypeId,
            fileName: uploadFile.name,
            fileData,
            mimeType: uploadFile.type,
            idempotencyKey,
        }, abort.signal);

        this.finishUpload();
        // Failed, cancelled or timed out: file and idempotency key stay, so a retry cannot duplicate.
        if (abort.signal.aborted) {
            toastService.info(t("uploadCancelled"));
            return;
        }
        if (!result.success) {
            // CRM refused the file (e.g. duplicate name), so this stops the upload too and stays until closed.
            toastService.error(this.errorMessage(result, "uploadNoResponse"), "Error", true);
            return;
        }

        toastService.success(t("documentUploaded"));
        this.setState((state) => ({
            uploadFile: null,
            uploadInputKey: state.uploadInputKey + 1,
            uploadDocumentTypeId: null,
            uploadIdempotencyKey: null,
        }));
        this.loadDocuments();
    };

    finishUpload = () => {
        this.uploadAbort = null;
        if (this.uploadSlowTimer) {
            clearTimeout(this.uploadSlowTimer);
            this.uploadSlowTimer = null;
        }
        this.setState({ isUploading: false, uploadSlow: false });
    };

    // No HTTP status means a timeout or network failure: axios's raw text ("timeout of 30000ms
    // exceeded") is logged, never shown.
    errorMessage = (result: Result<unknown>, noResponseKey: StringKey): string => {
        if (result.status === undefined) {
            console.warn("[UserDocuments] request got no response:", result.message);
            return t(noResponseKey);
        }
        return result.message;
    };

    // Every additional-document row has a stored file (CRM requires one on every create/upload path),
    // so Preview/Download gate on the permission alone; a missing object surfaces as a CRM error toast.
    onDownload = async (individualDocument: IndividualDocumentModel) => {
        this.setState({ downloadingDocumentId: individualDocument.id });
        const result = await individualDocumentApiService.getContent(individualDocument.id);
        this.setState({ downloadingDocumentId: null });
        if (!result.success) {
            toastService.error(this.errorMessage(result, "fileLoadNoResponse"));
            return;
        }
        const blob = new Blob([result.data], { type: individualDocument.mimeType || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", individualDocument.originalFileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Opens the dialog in its loading state; the returned controller is aborted when it is closed or replaced.
    startPreview = (target: PreviewTarget): AbortController => {
        this.stopPreviewRequest();
        this.revokePreviewUrl();
        const abort = new AbortController();
        this.previewAbort = abort;
        this.previewSlowTimer = setTimeout(() => this.setState({ previewSlow: true }), SLOW_AFTER_MS);
        this.setState({ previewDocument: target, previewUrl: null, previewLoading: true, previewSlow: false });
        return abort;
    };

    onPreview = async (individualDocument: IndividualDocumentModel) => {
        const abort = this.startPreview({
            fileName: individualDocument.originalFileName,
            mimeType: individualDocument.mimeType,
            download: () => this.onDownload(individualDocument),
        });

        const result = await individualDocumentApiService.getContent(individualDocument.id, abort.signal);
        // Closed (or another preview opened) while loading: drop the late response.
        if (abort.signal.aborted) {
            return;
        }
        this.stopPreviewRequest();
        this.setState({ previewLoading: false });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "fileLoadNoResponse"));
            this.setState({ previewDocument: null });
            return;
        }
        const blob = new Blob([result.data], { type: individualDocument.mimeType || "application/octet-stream" });
        this.setState({ previewUrl: URL.createObjectURL(blob) });
    };

    revokePreviewUrl = () => {
        if (this.state.previewUrl) {
            URL.revokeObjectURL(this.state.previewUrl);
        }
    };

    stopPreviewRequest = () => {
        this.previewAbort?.abort();
        this.previewAbort = null;
        if (this.previewSlowTimer) {
            clearTimeout(this.previewSlowTimer);
            this.previewSlowTimer = null;
        }
    };

    closePreview = () => {
        this.stopPreviewRequest();
        this.revokePreviewUrl();
        this.setState({ previewDocument: null, previewUrl: null, previewLoading: false, previewSlow: false });
    };

    onStartReview = async (document: IndividualDocumentModel) => {
        this.setState({ busyDocumentId: document.id });
        const result = await individualDocumentApiService.startReview(document.id, document.rowVersion ?? "");
        this.setState({ busyDocumentId: null });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "actionNoResponse"));
            return;
        }
        toastService.success(t("reviewStarted"));
        this.loadDocuments();
    };

    onConfirm = async (document: IndividualDocumentModel) => {
        this.setState({ busyDocumentId: document.id });
        const result = await individualDocumentApiService.confirm(document.id, { rowVersion: document.rowVersion ?? "" });
        this.setState({ busyDocumentId: null });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "actionNoResponse"));
            return;
        }
        toastService.success(t("documentConfirmed"));
        this.loadDocuments();
    };

    onReject = async (document: IndividualDocumentModel) => {
        const reason = this.state.rejectionDraftByDocumentId[document.id]?.trim();
        if (!reason) {
            toastService.error(t("enterRejectionReason"));
            return;
        }

        this.setState({ busyDocumentId: document.id });
        const result = await individualDocumentApiService.reject(document.id, {
            rowVersion: document.rowVersion ?? "",
            customerRejectionReason: reason,
        });
        this.setState({ busyDocumentId: null });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "actionNoResponse"));
            return;
        }
        toastService.success(t("documentRejected"));
        this.loadDocuments();
    };

    onSaveNote = async (document: IndividualDocumentModel) => {
        const note = this.state.noteDraftByDocumentId[document.id] ?? document.internalComplianceNote ?? "";

        this.setState({ busyDocumentId: document.id });
        const result = await individualDocumentApiService.updateInternalNote(document.id, {
            rowVersion: document.rowVersion ?? "",
            internalComplianceNote: note,
        });
        this.setState({ busyDocumentId: null });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "actionNoResponse"));
            return;
        }
        toastService.success(t("noteSaved"));
        this.loadDocuments();
    };

    onToggleVisibility = async (document: IndividualDocumentModel, isVisibleToCustomer: boolean) => {
        this.setState({ busyDocumentId: document.id });
        const result = await individualDocumentApiService.updateCustomerVisibility(document.id, {
            rowVersion: document.rowVersion ?? "",
            isVisibleToCustomer,
        });
        this.setState({ busyDocumentId: null });

        if (!result.success) {
            toastService.error(this.errorMessage(result, "actionNoResponse"));
            return;
        }
        toastService.success(isVisibleToCustomer ? t("visibleToCustomer") : t("hiddenFromCustomer"));
        this.loadDocuments();
    };

    renderWorkflowStatus = (status: AdditionalDocumentWorkflowStatus | null) => {
        if (status == null) {
            return <AppBadge variant="default">N/A</AppBadge>;
        }
        return <AppBadge variant={workflowStatusBadgeMap[status] as any}>{t(workflowStatusLabels[status])}</AppBadge>;
    };

    renderActions = (document: IndividualDocumentModel) => {
        const isBusy = this.state.busyDocumentId === document.id;
        const selfApproval = this.isSelfApproval(document);
        const canReview = isIndividualDocumentsReviewAllowed();
        const canApprove = isIndividualDocumentsApproveAllowed() && !selfApproval;
        const canDownload = isIndividualDocumentsDownloadAllowed();

        return (
            <div className="flex flex-column gap-2">
                {canDownload && (
                    <Button label={t("preview")} size="small" outlined onClick={() => this.onPreview(document)} />
                )}
                {canDownload && (
                    <Button
                        label={t("download")}
                        size="small"
                        outlined
                        loading={this.state.downloadingDocumentId === document.id}
                        onClick={() => this.onDownload(document)}
                    />
                )}
                {canReview && document.workflowStatus === AdditionalDocumentWorkflowStatus.Uploaded && (
                    <Button label={t("startReview")} size="small" loading={isBusy} onClick={() => this.onStartReview(document)} />
                )}
                {canApprove && document.workflowStatus === AdditionalDocumentWorkflowStatus.InReview && (
                    <>
                        <Button label={t("confirm")} size="small" severity="success" loading={isBusy} onClick={() => this.onConfirm(document)} />
                        <InputTextarea
                            rows={2}
                            placeholder={t("rejectionReasonPlaceholder")}
                            value={this.state.rejectionDraftByDocumentId[document.id] ?? ""}
                            onChange={(e) =>
                                this.setState((prev) => ({
                                    rejectionDraftByDocumentId: { ...prev.rejectionDraftByDocumentId, [document.id]: e.target.value },
                                }))
                            }
                        />
                        <Button label={t("reject")} size="small" severity="danger" loading={isBusy} onClick={() => this.onReject(document)} />
                    </>
                )}
                {!canApprove && selfApproval && document.workflowStatus === AdditionalDocumentWorkflowStatus.InReview && (
                    <span style={{ color: "#EF4444", fontSize: 12 }}>{t("cannotApproveOwn")}</span>
                )}
                {(isBusy || this.state.downloadingDocumentId === document.id) && this.state.waitSlow && (
                    <span style={{ color: "#6B7280", fontSize: 12 }}>{t("stillWorking")}</span>
                )}
            </div>
        );
    };

    renderPreviewDialog = () => {
        const { previewDocument, previewUrl, previewLoading } = this.state;
        const isImage = previewDocument?.mimeType?.startsWith("image/") ?? false;
        const isPdf = previewDocument?.mimeType === "application/pdf";

        return (
            <Dialog
                header={previewDocument?.fileName ?? "Preview"}
                visible={!!previewDocument}
                style={{ width: "70vw" }}
                contentStyle={{ minHeight: "50vh" }}
                modal
                onHide={this.closePreview}
            >
                {previewLoading && <p>{t(this.state.previewSlow ? "previewStillLoading" : "previewLoading")}</p>}
                {!previewLoading && previewUrl && isImage && (
                    <img src={previewUrl} alt={previewDocument!.fileName} style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto" }} />
                )}
                {!previewLoading && previewUrl && isPdf && (
                    <iframe src={previewUrl} title={previewDocument!.fileName} style={{ width: "100%", height: "70vh", border: "none" }} />
                )}
                {!previewLoading && previewUrl && !isImage && !isPdf && (
                    <div>
                        <p>{t("previewNotAvailable")} ({previewDocument?.mimeType}).</p>
                        <Button label={t("downloadInstead")} size="small" onClick={() => previewDocument!.download()} />
                    </div>
                )}
            </Dialog>
        );
    };

    // A legacy row (no workflow status) is read-only: no note, no visibility, no workflow actions.
    renderInternalNote = (document: IndividualDocumentModel) => {
        if (document.workflowStatus == null) {
            return "N/A";
        }
        if (!isIndividualDocumentsManageInternalNoteAllowed()) {
            return document.internalComplianceNote ?? "";
        }
        const disabled = isDecided(document);
        return (
            <div className="flex flex-column gap-2">
                <InputTextarea
                    rows={2}
                    disabled={disabled}
                    value={this.state.noteDraftByDocumentId[document.id] ?? document.internalComplianceNote ?? ""}
                    onChange={(e) =>
                        this.setState((prev) => ({
                            noteDraftByDocumentId: { ...prev.noteDraftByDocumentId, [document.id]: e.target.value },
                        }))
                    }
                />
                <Button label={t("saveNote")} size="small" outlined disabled={disabled} onClick={() => this.onSaveNote(document)} />
                {disabled && <span style={{ fontSize: 12, color: "#6B7280" }}>{t("readOnlyDecided")}</span>}
            </div>
        );
    };

    renderVisibility = (document: IndividualDocumentModel) => {
        if (document.workflowStatus == null) {
            return "N/A";
        }
        if (!isIndividualDocumentsManageCustomerVisibilityAllowed()) {
            return document.isVisibleToCustomer ? "Yes" : "No";
        }
        return (
            <Checkbox
                checked={!!document.isVisibleToCustomer}
                disabled={isDecided(document)}
                onChange={(e) => this.onToggleVisibility(document, !!e.checked)}
            />
        );
    };

    renderUploadForm = () => {
        if (!isIndividualDocumentsUploadAllowed()) {
            return null;
        }
        return (
            <div className="flex align-items-end gap-2 mb-3" style={{ flexWrap: "wrap" }}>
                <div>
                    <input key={this.state.uploadInputKey} type="file" accept={ALLOWED_UPLOAD_EXTENSIONS.join(",")} onChange={this.onUploadFileChange} disabled={this.state.isUploading} />
                </div>
                {this.state.typesLoadFailed && (
                    <div className="flex align-items-center gap-2">
                        <span>{t("typesLoadFailed")}</span>
                        <Button label={t("retry")} type="button" size="small" outlined onClick={this.loadDocumentTypes} />
                    </div>
                )}
                <div style={{ minWidth: 220 }}>
                    <ExtendedDropdown
                        value={this.state.uploadDocumentTypeId}
                        options={this.state.documentTypes}
                        optionLabel="displayName"
                        optionValue="id"
                        filter
                        filterBy="displayName"
                        placeholder={t("selectDocumentType")}
                        disabled={this.state.isUploading}
                        // CRM answers 409 when a key is reused with different data, so a new type means a new key.
                        onChange={(e) => this.setState({ uploadDocumentTypeId: e.value, uploadIdempotencyKey: null })}
                    />
                </div>
                <Button label={t("upload")} loading={this.state.isUploading} onClick={this.onUpload} />
                {this.state.isUploading && (
                    <>
                        <Button label={t("cancel")} type="button" outlined onClick={() => this.uploadAbort?.abort()} />
                        {this.state.uploadSlow && <span>{t("stillUploading")}</span>}
                    </>
                )}
            </div>
        );
    };

    renderKycDocuments = () => {
        if (!this.props.currentKycSubmissionId) {
            return null;
        }
        const documentTypeNameByCode: Record<string, string> = {};
        this.state.documentTypes.forEach((documentType) => {
            documentTypeNameByCode[documentType.code] = documentType.displayName;
        });
        const canDownload = isIndividualDocumentsDownloadAllowed();

        return (
            <div className="mb-4">
                <h4 className="mb-2">{t("kycDocumentsTitle")}</h4>
                {this.state.kycDocumentsLoadFailed && (
                    <div className="flex align-items-center gap-2 mb-2">
                        <span>{t("kycDocumentsLoadFailed")}</span>
                        <Button label={t("retry")} type="button" size="small" outlined onClick={this.loadKycDocuments} />
                    </div>
                )}
                <DataTable value={this.state.kycDocuments}>
                    <Column field="fileName" header="File Name" />
                    <Column
                        field="documentType"
                        header="Document Type"
                        body={(rowData: KycSubmissionDocument) => documentTypeNameByCode[rowData.documentType] ?? rowData.documentType}
                    />
                    <Column field="uploadStamp" header="Uploaded" body={dateBodyTemplate("uploadStamp")} />
                    <Column
                        header="Actions"
                        body={(rowData: KycSubmissionDocument) =>
                            canDownload && (
                                <div className="flex gap-2">
                                    <Button label={t("preview")} size="small" outlined onClick={() => this.onPreviewKycDocument(rowData)} />
                                    <Button
                                        label={t("download")}
                                        size="small"
                                        outlined
                                        loading={this.state.openingKycFileStorageId === rowData.fileStorageId}
                                        onClick={() => this.onDownloadKycDocument(rowData)}
                                    />
                                </div>
                            )
                        }
                    />
                </DataTable>
            </div>
        );
    };

    render() {
        return (
            <div>
                {this.renderPreviewDialog()}
                {this.renderKycDocuments()}
                {this.renderUploadForm()}
                {this.state.isLoading && this.state.waitSlow && (
                    <div className="mb-2" style={{ color: "#6B7280" }}>{t("stillLoading")}</div>
                )}
                {this.state.loadFailed && (
                    <div className="flex align-items-center gap-2 mb-3">
                        <span>{t("listLoadFailed")}</span>
                        <Button label={t("retry")} type="button" size="small" outlined onClick={() => this.loadDocuments()} />
                    </div>
                )}
                <DataTable loading={this.state.isLoading} value={this.state.documents}>
                    <Column field="originalFileName" header="File Name" />
                    <Column
                        field="documentTypeId"
                        header="Document Type"
                        body={(rowData: IndividualDocumentModel) =>
                            rowData.documentTypeId != null
                                ? this.state.documentTypeNameById[rowData.documentTypeId] ?? rowData.documentTypeId
                                : rowData.documentTypeDisplayName || "N/A"
                        }
                    />
                    <Column
                        field="workflowStatus"
                        header="Status"
                        body={(rowData: IndividualDocumentModel) => this.renderWorkflowStatus(rowData.workflowStatus)}
                    />
                    <Column field="uploadStamp" header="Uploaded" body={dateBodyTemplate("uploadStamp")} />
                    <Column field="uploadedBy" header="Uploaded By" />
                    <Column
                        field="uploadChannel"
                        header="Upload Channel"
                        body={(rowData: IndividualDocumentModel) =>
                            rowData.uploadChannel != null ? uploadChannelLabels[rowData.uploadChannel] : "N/A"
                        }
                    />
                    <Column
                        field="relatedKycSubmissionId"
                        header="Related KYC Submission"
                        body={(rowData: IndividualDocumentModel) => rowData.relatedKycSubmissionId ?? "N/A"}
                    />
                    <Column
                        field="isVisibleToCustomer"
                        header="Visible to Customer"
                        body={(rowData: IndividualDocumentModel) => this.renderVisibility(rowData)}
                    />
                    <Column
                        field="customerRejectionReason"
                        header="Rejection Reason"
                        body={(rowData: IndividualDocumentModel) => rowData.customerRejectionReason ?? "N/A"}
                    />
                    <Column
                        field="internalComplianceNote"
                        header="Internal Compliance Note"
                        body={(rowData: IndividualDocumentModel) => this.renderInternalNote(rowData)}
                    />
                    <Column header="Actions" body={(rowData: IndividualDocumentModel) => this.renderActions(rowData)} />
                </DataTable>
                {(this.state.pageNumber > 1 || this.state.documents.length === DOCUMENTS_PAGE_SIZE) && (
                    <div className="flex justify-content-end align-items-center gap-2 mt-2">
                        <Button
                            label={t("previousPage")}
                            size="small"
                            outlined
                            disabled={this.state.isLoading || this.state.pageNumber <= 1}
                            onClick={() => this.loadDocuments(this.state.pageNumber - 1)}
                        />
                        <span>{this.state.pageNumber}</span>
                        <Button
                            label={t("nextPage")}
                            size="small"
                            outlined
                            disabled={this.state.isLoading || this.state.documents.length < DOCUMENTS_PAGE_SIZE}
                            onClick={() => this.loadDocuments(this.state.pageNumber + 1)}
                        />
                    </div>
                )}
            </div>
        );
    }
}
